// Push-Mitteilungen fürs Konto (Task 11, docs/superpowers/plans/2026-09-04-
// community-workflow-round.md): Berechtigung einholen, beim Push-Dienst des
// Browsers anmelden, den Endpunkt beim Server hinterlegen -- Abmelden
// spiegelbildlich. ENV-gated auf dem Server (VAPID_PUBLIC_KEY/
// VAPID_PRIVATE_KEY, config/vapid_settings.rb): ohne Schlüssel gibt es weder
// die Karte auf /konto noch den Meta-Tag, den dieses Modul braucht, und
// `startPushSettings` findet dann gar nichts zum Anmelden.
//
// Dieselbe "best effort"-Haltung wie serviceWorker.ts: ein Browser ohne
// Push API, eine verweigerte Berechtigung, ein Netzwerkfehler -- nichts
// davon darf die Kontoseite drumherum stören. Und dieselbe Bauart wie dort:
// reine, injizierbare Funktionen für die Logik (hier prüfbar ohne echten
// Service-Worker), eine Wiring-Funktion für den Rest.

import { apiDelete, apiSendJson } from './api'

export const VAPID_META = 'vapid-public-key'

/** Der öffentliche Schlüssel aus dem Meta-Tag, oder null ohne einen. */
export function vapidPublicKey(metaContent: string | null | undefined): string | null {
  const key = metaContent?.trim() ?? ''
  return key.length > 0 ? key : null
}

/**
 * Base64url (wie `WebPush.generate_key` ihn ausgibt) in die Bytes, die
 * `pushManager.subscribe` als `applicationServerKey` erwartet.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)

  // `new Uint8Array(length)`, not `.from`: the latter's return type is
  // generic over `ArrayBufferLike` (which also covers `SharedArrayBuffer`),
  // and `pushManager.subscribe` wants an `ArrayBufferView<ArrayBuffer>` --
  // exactly what allocating by length gives.
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)

  return bytes
}

export type PushSubscriptionLike = {
  endpoint: string
  unsubscribe(): Promise<boolean>
  toJSON(): { keys?: { p256dh?: string; auth?: string } }
}

export type SubscribeOptions = { userVisibleOnly: boolean; applicationServerKey: Uint8Array<ArrayBuffer> }

export type PushManagerLike = {
  getSubscription(): Promise<PushSubscriptionLike | null>
  subscribe(options: SubscribeOptions): Promise<PushSubscriptionLike>
}

export type RegistrationLike = { pushManager: PushManagerLike }

function subscriptionParams(subscription: PushSubscriptionLike): { endpoint: string; p256dh: string; auth: string } {
  const keys = subscription.toJSON().keys ?? {}
  return { endpoint: subscription.endpoint, p256dh: keys.p256dh ?? '', auth: keys.auth ?? '' }
}

/** Ob Service-Worker und Push API überhaupt da sind, bevor irgendetwas versucht wird. */
export function pushSupported(
  container: { serviceWorker?: unknown } | undefined,
  hasPushManager: boolean,
): boolean {
  return container !== undefined && 'serviceWorker' in container && hasPushManager
}

/**
 * Anmelden: Berechtigung einholen, beim Push-Dienst des Browsers abonnieren,
 * den Endpunkt beim Server hinterlegen. `false`, ohne dass etwas kaputt
 * ist, wenn die Berechtigung fehlt -- ein Nein ist hier eine gültige Antwort.
 */
export async function subscribeToPush(
  registration: RegistrationLike,
  publicKey: string,
  requestPermission: () => Promise<NotificationPermission>,
): Promise<boolean> {
  const permission = await requestPermission()
  if (permission !== 'granted') return false

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  await apiSendJson('/api/v1/push_subscriptions', 'POST', subscriptionParams(subscription))

  return true
}

/**
 * Abmelden: beim Push-Dienst und beim Server, in der Reihenfolge -- der
 * Endpunkt wird erst gelesen, dann steht `unsubscribe()` ihn nicht mehr zur
 * Verfügung.
 */
export async function unsubscribeFromPush(registration: RegistrationLike): Promise<boolean> {
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return false

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  await apiDelete(`/api/v1/push_subscriptions?endpoint=${encodeURIComponent(endpoint)}`)

  return true
}

export type ServiceWorkerContainerLike = { getRegistration?(): Promise<RegistrationLike | undefined> }

/**
 * Aufräumen für Abmelden und Kontowechsel -- dieselben zwei Auslöser wie
 * `serviceWorker.ts#clearOfflineData` (Caches, IndexedDB), die diese
 * Funktion von dort aus zusätzlich aufruft. Web-Push überlebt die
 * Rails-Sitzung: ohne dieses Aufräumen bekäme ein zweites Konto auf
 * demselben Browser (Abmelden, geteiltes Gerät) weiter die Mitteilungen
 * des ersten, ohne dass es je selbst "Aktivieren" gedrückt hätte.
 *
 * Fire-and-forget mit Absicht, wie beim Aufrufer: im Abmelde-Formular läuft
 * das hier VOR der Navigation, während die Sitzung, unter der `DELETE
 * /api/v1/push_subscriptions` noch authentifiziert wäre, noch gültig ist --
 * die Navigation wartet aber nicht auf das Ergebnis, und der Browser kann
 * die Anfrage abbrechen, bevor sie ankommt. Best effort: ein Browser ohne
 * Registrierung, ohne Abonnement oder eine abgebrochene Anfrage darf das
 * Abmelden nie aufhalten oder zum Fehler machen, deshalb der Fang um alles.
 */
export async function clearPushSubscription(container: ServiceWorkerContainerLike | undefined): Promise<void> {
  if (!container?.getRegistration) return

  try {
    const registration = await container.getRegistration()
    if (!registration) return

    await unsubscribeFromPush(registration)
  } catch (error) {
    console.warn('Push subscription did not clear', error)
  }
}

/** Ob dieser Browser gerade schon angemeldet ist -- für den ersten Anzeigezustand der Karte. */
export async function currentlySubscribed(registration: RegistrationLike): Promise<boolean> {
  return (await registration.pushManager.getSubscription()) !== null
}

function metaContent(name: string): string | null {
  return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.getAttribute('content') ?? null
}

/**
 * Der Einstieg aus dem Entrypoint: Karte suchen, Unterstützung prüfen,
 * Knöpfe verdrahten. Bleibt beides `hidden` (das Markup-Standard,
 * accounts/_push_notifications.html.erb), wenn irgendetwas hier scheitert
 * oder der Browser nicht kann -- ein Knopf, der nichts einlösen könnte,
 * wäre schlimmer als kein Knopf.
 */
export async function startPushSettings(root: ParentNode = document): Promise<void> {
  const card = root.querySelector<HTMLElement>('[data-push-card]')
  if (!card) return

  const nav = typeof navigator === 'undefined' ? undefined : navigator
  const hasPushManager = typeof PushManager !== 'undefined'
  if (!pushSupported(nav, hasPushManager) || nav === undefined) {
    card.hidden = true
    return
  }

  const publicKey = vapidPublicKey(metaContent(VAPID_META))
  if (!publicKey) {
    card.hidden = true
    return
  }

  const subscribeButton = card.querySelector<HTMLButtonElement>('[data-push-subscribe]')
  const unsubscribeButton = card.querySelector<HTMLButtonElement>('[data-push-unsubscribe]')
  const status = card.querySelector<HTMLElement>('[data-push-status]')

  const setState = (subscribed: boolean, message = ''): void => {
    if (subscribeButton) subscribeButton.hidden = subscribed
    if (unsubscribeButton) unsubscribeButton.hidden = !subscribed
    if (status) status.textContent = message
  }

  try {
    const registration = await nav.serviceWorker.ready
    setState(await currentlySubscribed(registration))

    subscribeButton?.addEventListener('click', () => {
      void subscribeToPush(registration, publicKey, () => Notification.requestPermission())
        .then((granted) => setState(granted, granted ? '' : 'Ohne Berechtigung keine Mitteilungen.'))
        .catch(() => setState(false, 'Anmelden hat nicht geklappt.'))
    })

    unsubscribeButton?.addEventListener('click', () => {
      void unsubscribeFromPush(registration)
        .then(() => setState(false))
        .catch(() => setState(true, 'Abmelden hat nicht geklappt.'))
    })
  } catch (error) {
    console.warn('Push notifications did not start', error)
    card.hidden = true
  }
}
